import { useEffect, useRef } from 'react';

import {
	type InsightsBannerResolution,
	type InsightsBannerSurface,
	resolveInsightsBannerVisibility,
	type ResolveInsightsBannerVisibilityInput,
} from '../insights-banner-visibility';

import { useFireAnalyticsEvent } from './use-fire-analytics';

export interface ReportInsightsBannerResolvedInput extends ResolveInsightsBannerVisibilityInput {
	surface: InsightsBannerSurface;
	/** UTC ISO-8601 timestamp the client saw, for reconciliation against backend logs. */
	generatedAt?: string;
}

/**
 * Reports a single `operational insightsBanner resolved` event once the visibility decision
 * has settled — carrying `shown` and the full `reason` taxonomy so one event powers the
 * shown/not-shown funnel and its by-reason breakdown. `operational` classifies it as the
 * client diagnostic it is; all events reach Socrates regardless of type. Fires at most once
 * per mount; inputs settle asynchronously, so the ref guards against re-firing.
 */
export const useReportInsightsBannerResolved = (
	input: ReportInsightsBannerResolvedInput,
): InsightsBannerResolution => {
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();
	const reportedRef = useRef(false);

	const resolution = resolveInsightsBannerVisibility(input);
	const { shown, reason } = resolution;

	const {
		surface,
		scheduleOk,
		hasProduct,
		isInsightsAvailable,
		isFresh,
		requiresFresh,
		hasSelectedSpace,
		statusFetchState,
		generatedAt,
	} = input;

	useEffect(() => {
		if (reportedRef.current || reason === null) {
			return;
		}
		reportedRef.current = true;
		fireAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'insightsBanner',
			action: 'resolved',
			attributes: {
				shown,
				reason,
				surface,
				scheduleOk,
				hasProduct,
				insightsAvailable: isInsightsAvailable,
				isFresh,
				requiresFresh,
				hasSelectedSpace,
				statusFetchState,
				generatedAt: generatedAt ?? null,
			},
		});
	}, [
		shown,
		reason,
		surface,
		scheduleOk,
		hasProduct,
		isInsightsAvailable,
		isFresh,
		requiresFresh,
		hasSelectedSpace,
		statusFetchState,
		generatedAt,
		fireAnalyticsEvent,
	]);

	return resolution;
};
